import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { SessionContext } from "../../context/SessionContext";
import { currentProfile } from "../../api/mw/client";
import { getContentFilter, setContentFilter } from "../../api/mw/contentFilter";
import { loadRequest } from "../../utils/loadRequest";
import { flowCopy } from "../../utils/flowCopy";
import { contentFilterCopy } from "./contentFilterCopy";
import "./ContentFilterSettings.scss";

function ContentFilterForm({ profileId }) {
  const [preferences, setPreferences] = useState(null);
  const [operation, setOperation] = useState("loading");
  const busy = operation !== null;
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const mounted = useRef(true);
  const pending = useRef(false);
  const copy = contentFilterCopy();
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = async () => {
    if (pending.current || !mounted.current) return;
    pending.current = true;
    setOperation("loading");
    setSaved(false);
    setError(null);
    try {
      const { data } = await loadRequest((options) =>
        getContentFilter(profileId, options)
      );
      if (mounted.current) setPreferences(data);
    } catch (_) {
      if (mounted.current) {
        setPreferences(null);
        setError(flowCopy().loadError);
      }
    } finally {
      pending.current = false;
      if (mounted.current) setOperation(null);
    }
  };

  useEffect(() => {
    refresh();
    const onFocus = () => {
      if (document.visibilityState !== "hidden") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // Each profile gets a separate form; its requests cannot update the next one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = async (showUnentitled) => {
    if (
      pending.current ||
      !preferences ||
      preferences.locked ||
      preferences.showUnentitled === showUnentitled
    )
      return;
    pending.current = true;
    setOperation("saving");
    setSaved(false);
    setError(null);
    try {
      const { data } = await setContentFilter(
        profileId,
        showUnentitled,
        () => mounted.current
      );
      if (!mounted.current) return;
      setPreferences(data);
      setSaved(!data.locked && data.showUnentitled === showUnentitled);
    } catch (_) {
      // The write may have committed. Require a fresh server read, not a retry
      // against an old value or a potentially newly locked central rule.
      if (mounted.current) {
        setPreferences(null);
        setError(copy.saveError);
      }
    } finally {
      pending.current = false;
      if (mounted.current) setOperation(null);
    }
  };

  return (
    <section className="content-filter-settings" aria-busy={busy}>
      <div className="content-filter-control">
        <label htmlFor="show-unentitled">{copy.label}</label>
        <div className="content-filter-toggle">
          <span className="content-filter-value" aria-hidden="true">
            {preferences ? (preferences.showUnentitled ? copy.on : copy.off) : "—"}
          </span>
          <span className="content-filter-switch">
          <input
          id="show-unentitled"
          type="checkbox"
          role="switch"
          aria-describedby={`content-filter-description${
            preferences?.locked ? " content-filter-lock" : ""
          }`}
          aria-checked={preferences?.showUnentitled === true}
          checked={preferences?.showUnentitled === true}
          disabled={busy || !preferences || preferences.locked}
          onChange={(event) => change(event.target.checked)}
        />
          <span className="content-filter-track" aria-hidden="true" />
          </span>
        </div>
      </div>
      <p id="content-filter-description">{copy.description}</p>
      {preferences && !busy && (
        <p className="content-filter-result" role="status">
          {preferences.showUnentitled ? copy.showingAll : copy.showingIncluded}
        </p>
      )}
      {preferences?.locked && (
        <p
          id="content-filter-lock"
          className="content-filter-lock"
          role="status"
        >
          {copy.locked}
        </p>
      )}
      {busy && (
        <p role="status">{operation === "saving" ? copy.saving : flowCopy().loading}</p>
      )}
      {saved && <p role="status">{copy.saved}</p>}
      {error && <p role="alert">{error}</p>}
      <button
        type="button"
        className="content-filter-refresh"
        disabled={busy}
        onClick={refresh}
      >
        {copy.refresh}
      </button>
    </section>
  );
}

export default function ContentFilterSettings() {
  const { profile } = useContext(SessionContext) || {};
  const profileId = profile?.id || currentProfile().id;
  return (
    <ContentFilterForm key={profileId || "missing"} profileId={profileId} />
  );
}
